import React, { FunctionComponent } from 'react';
import { map, isEmpty, values, filter, has, addIndex, flatten } from 'ramda';
import { Container, Header, Button, Icon, Grid } from 'semantic-ui-react';

import { DefaultError } from 'scenes/Report/components/Query/Query.helpers';
import ParamsHeader, {
  ReportParams,
} from 'scenes/Report/components/ReportBase/components/ParamsHeader';
import { ClassProp } from 'helpers/types';
import { BatchData } from 'components/DownloadWidget/DownloadWidget';

import FailedReport from './components/FailedReport';
import ValidReport from './components/ValidReport';
import withStyles from 'react-jss';

const styles = {
  container: {
    padding: '4rem',
    margin: '12.5rem 0 4rem 0',
    background: '#fff',
    borderRadius: '0.25em',
    boxShadow:
      '0 0.0625em 0 0 rgba(0,0,0,0.03), 0 0.125em 0.375em 0 rgba(0,0,0,0.06);',
  },
  title: {
    marginTop: '3rem !important',
    marginBottom: '2rem !important',
  },
};

export type ReportProps = {
  pageId: string;
  name: string;
  imageName: string;
  imageURL?: string;
  imageBlob?: Blob;
  additionalOptions?: {
    transform: Record<string, unknown>;
    size: Record<string, unknown>;
  };
  publicImageURL?: string;
  error?: DefaultError;
  data?: BatchData | any[];
};

type Props = {
  classes: ClassProp<typeof styles>;
  params: ReportParams;
  validReports: Record<string, unknown>;
  invalidReports: Record<string, unknown>[];
  templateURL: string;
};

const mapIndexed: any = addIndex<any>(map);

const filterFailedReports = filter(has('error'));

const renderFailedReports = mapIndexed(
  ({ name, error }: ReportProps, index: number) => (
    <FailedReport key={`${name}_${index}`} name={name} error={error} />
  ),
);

const renderValidReports = mapIndexed(
  ({ imageURL, name }: ReportProps, index: number) => (
    <ValidReport key={`${name}_${index}`} name={name} imageURL={imageURL} />
  ),
);

const GenerationResults: FunctionComponent<Props> = ({
  classes,
  params,
  validReports,
  invalidReports,
  templateURL,
}) => {
  const failedReports = filterFailedReports(invalidReports) as ReportProps[];
  return (
    <Container className={classes.container}>
      <ParamsHeader reportParams={params} />

      <Header as="h4" className={classes.title}>
        Click on Google Slide button to access the generated file.
      </Header>

      <Button as="a" href={templateURL} target="__blank" color="yellow">
        <Icon name="google drive" /> Google Slide Presentation
      </Button>

      {!isEmpty(validReports) && (
        <Header className={classes.title} as="h4">
          Or download the images separately:
        </Header>
      )}

      <Grid>{renderValidReports(flatten(values(validReports)))}</Grid>

      {!isEmpty(failedReports) && (
        <Header as="h4" className={classes.title}>
          We weren&apos;t able to generate some of the reports properly. Click
          name to see the details:
        </Header>
      )}

      <Grid>{renderFailedReports(failedReports)}</Grid>
    </Container>
  );
};

export default withStyles(styles)(GenerationResults);
